import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import Seo from "../components/Seo";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Download, Lock, Heart, Mail } from "lucide-react";

/* /free-book — The FREE GIVEAWAY guide. Visitor drops email, gets Mike's
   "FAITHARAMA FREE GUIDE" PDF. No card. No paid upsell on this page. */

const WINE  = "#0F172A";
const O     = "#F39A1F";
const NAVY = "#0F172A";
const GOLD  = "#E8B539";
const CREAM = "#FFFFFF";
const INK   = "#0F172A";
const GREEN = "#2E9F3A";

const COVER_IMG     = "/free-guide/free-guide-cover.png";
const PREVIEW_IMG   = "/free-guide/guide-images-composite.png";
const PDF_URL       = "/faitharama-free-guide.pdf";

export default function FreeBookPage() {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return toast.error("Please add your name and email.");
    setSubmitting(true);
    try {
      await api.post("/leads/free-guide", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        source: ref ? `free-book?ref=${ref}` : "free-book",
      });
      setDone(true);
      const a = document.createElement("a");
      a.href = PDF_URL;
      a.download = "Faitharama-Free-Guide.pdf";
      a.click();
    } catch (_err) {
      toast.error("Couldn't send. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="free-book-page">
      <Seo
        title="Faitharama Free Guide — See What We Built (Free PDF)"
        description="Drop your email and we'll send you the Faitharama Free Guide — a picture-first look at every tool inside Faitharama. No card. No spam."
        canonical="https://faitharama.com/free-book"
      />
      <SiteNav />

      {/* ───────── HERO: Mike's real cover + email capture ───────── */}
      <section className="border-b-4" style={{ background: "#EAF6FC", borderColor: O }}>
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16 grid md:grid-cols-2 gap-10 items-center">

          {/* Real cover — the one Mike sent */}
          <div className="flex justify-center">
            <div className="relative w-full max-w-[420px]">
              <div className="absolute -inset-6 rounded-2xl blur-3xl opacity-30"
                   style={{ background: `radial-gradient(circle, ${O} 0%, transparent 70%)` }}/>
              <img src={COVER_IMG}
                   alt="Faitharama Free Guide"
                   className="relative w-full h-auto rounded-xl shadow-2xl"
                   style={{ border: `4px solid ${O}`, filter: "drop-shadow(0 18px 40px rgba(0,0,0,0.25))" }}
                   data-testid="free-book-cover-img"/>
            </div>
          </div>

          {/* Form side */}
          <div style={{ color: INK }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.32em] uppercase font-bold mb-3 text-white"
                 style={{ background: GREEN }}>
              ★ 100% FREE · NO CARD · INSTANT DOWNLOAD
            </div>
            <h1 className="font-serif-display text-4xl md:text-5xl uppercase leading-[1.05]"
                style={{ color: WINE, textWrap: "balance" }}>
              See <span style={{ color: O }}>Everything</span> Inside Faitharama —<br/>
              In Pictures.
            </h1>
            <p className="mt-4 text-lg md:text-xl" style={{ color: INK }}>
              No paragraphs. No tech talk. Just pictures of every tool, what it does, and how regular folks use it. Read it in 10 minutes — keep it forever.
            </p>

            {done ? (
              <div className="mt-6 rounded-xl p-5 shadow-2xl"
                   style={{ background: "#FFFFFF", border: `3px solid ${GREEN}` }}
                   data-testid="free-book-success">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-7 h-7" style={{ color: GREEN }}/>
                  <h3 className="font-serif-display text-2xl uppercase" style={{ color: WINE }}>You're All Set!</h3>
                </div>
                <p className="text-sm mb-3" style={{ color: INK }}>
                  Your free guide is downloadin' now. We emailed you a copy too in case you need it later.
                </p>
                <a href={PDF_URL} download="Faitharama-Free-Guide.pdf"
                   data-testid="free-book-redownload"
                   className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.16em] text-white shadow"
                   style={{ background: WINE }}>
                  <Download className="w-4 h-4"/> Download Again
                </a>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="free-book-form"
                    className="mt-6 rounded-xl p-5 shadow-2xl space-y-3"
                    style={{ background: "#FFFFFF", border: `3px solid ${O}` }}>
                <input value={name} onChange={(e) => setName(e.target.value)}
                       data-testid="free-book-name"
                       placeholder="First Name"
                       className="w-full px-3 py-3 rounded-md text-base outline-none focus:ring-2"
                       style={{ background: CREAM, border: `2px solid ${O}55`, color: WINE }}/>
                <input value={email} onChange={(e) => setEmail(e.target.value)}
                       data-testid="free-book-email"
                       type="email"
                       placeholder="Email"
                       className="w-full px-3 py-3 rounded-md text-base outline-none focus:ring-2"
                       style={{ background: CREAM, border: `2px solid ${O}55`, color: WINE }}/>
                <button type="submit" disabled={submitting}
                        data-testid="free-book-submit"
                        className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-md text-base font-bold uppercase tracking-[0.16em] text-white shadow-lg hover:scale-[1.02] transition disabled:opacity-50"
                        style={{ background: WINE }}>
                  {submitting ? "Sendin'…" : <>Send Me The Free Guide <ArrowRight className="w-4 h-4"/></>}
                </button>
                <p className="text-[11px] italic text-center flex items-center justify-center gap-1.5" style={{ color: INK }}>
                  <Lock className="w-3 h-3"/> No spam · No card · Unsubscribe any time
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ───────── PEEK INSIDE — Mike's real guide pages ───────── */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="text-center mb-9">
          <div className="text-[11px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>
            ★ PEEK INSIDE THE FREE GUIDE
          </div>
          <h2 className="font-serif-display text-3xl md:text-5xl uppercase leading-tight mt-2"
              style={{ color: WINE, textWrap: "balance" }}>
            This Is What You'll See.
          </h2>
          <div className="h-1 w-16 mt-4 mx-auto" style={{ background: O }}/>
          <p className="text-base md:text-lg mt-5 max-w-2xl mx-auto" style={{ color: INK }}>
            Every page is a picture. Every picture shows you one tool, one feature, or one way Faitharama helps you.
          </p>
        </div>

        {/* The composite — Mike's actual guide pages laid out */}
        <div className="rounded-2xl overflow-hidden shadow-2xl border-4"
             style={{ borderColor: GOLD, background: "#FFFFFF" }}>
          <img src={PREVIEW_IMG}
               alt="All the pages inside the Faitharama Free Guide — what is Faitharama, why Mike built it, the Christian Entrepreneur Starter Kit, your office, the funnel builder, AI Writer, Ask Mike, book covers, calendar, lead scraper, earnings, and the community."
               loading="lazy"
               className="block w-full h-auto"
               data-testid="free-book-preview-composite"/>
        </div>

        {/* What you'll learn — small text list */}
        <div className="mt-12 max-w-3xl mx-auto">
          <h3 className="font-serif-display text-2xl md:text-3xl uppercase text-center"
              style={{ color: WINE }}>
            What's Covered (Picture By Picture)
          </h3>
          <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
            {[
              "What Faitharama Actually Is",
              "Why Faitharama Was Built",
              "The Christian Entrepreneur Starter Kit",
              "Your Digital Ministry Office",
              "The Funnel Builder (5 Designs)",
              "Your Live Page — Live Examples",
              "The AI Writer Tool",
              "The 24/7 Christian AI Mentor",
              "The Book Cover Creator",
              "Appointments + Smart Calendar",
              "How You Earn Real Income",
              "The Faitharama Community",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2.5 text-base md:text-lg" style={{ color: INK }}>
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-1" style={{ color: GREEN }}/>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom CTA */}
        {!done && (
          <div className="mt-12 text-center">
            <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
               data-testid="free-book-bottom-cta"
               className="inline-flex items-center gap-2 px-9 py-4 rounded-md text-base md:text-lg font-bold uppercase tracking-[0.18em] text-white shadow-2xl hover:scale-[1.03] transition"
               style={{ background: O }}>
              <Mail className="w-5 h-5"/> Send Me The Free Guide
            </a>
            <div className="mt-3 text-xs uppercase tracking-wider flex items-center justify-center gap-1.5" style={{ color: INK, opacity: 0.7 }}>
              <Lock className="w-3 h-3"/> No card · No spam · Unsubscribe any time
            </div>
          </div>
        )}
      </section>

      {/* ───────── PROMISE FROM FAITHARAMA ───────── */}
      <section className="border-t-2" style={{ background: "#EAF6FC", borderColor: `${O}55` }}>
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 text-center">
          <Heart className="w-7 h-7 mx-auto mb-3" style={{ color: WINE }}/>
          <p className="text-base md:text-lg italic leading-relaxed" style={{ color: WINE }}>
            "We built this guide because seniors deserve a website they can actually understand.
            Look at the pictures. If Faitharama looks like somethin' you'd use, drop your email
            and the whole guide's yours — no card, no spam, no tricks."
          </p>
          <div className="mt-4 text-[11px] tracking-[0.28em] uppercase font-bold" style={{ color: WINE }}>
            — Faitharama.com
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
